// [phase12cfe-v1] Signal Lane panel + Outcome Intelligence dashboard.
//
// Adds three independent lane sections (Micro / Small / Large) at the top
// of The Signal scoreboard, plus an Outcome Intelligence panel that
// consumes the legacy /api/v1/signal/outcome-learning/summary endpoint.
//
// Phase 12CF-E repair: each lane card now renders the actual candidate
// SYMBOL TABLE (rank, symbol, price, score, market-cap estimate),
// grouped strictly by `lane_type` before any display slicing. Show
// more / show less toggles the table to top-5 vs the full lane.
//
// Honest-empty / advisory-only contracts:
//   * Lane card carries advisory_only banner.
//   * 5-day / 10-day rates render as "Pending" when null — pending state
//     never suppresses candidate visibility.
//   * Combined pool is never rendered; each lane is fetched grouped
//     and rendered as its own section.
//
// Marker strings required by /etc/adversyn/trading-ui-markers.json:
//   - "Signal Lane Performance"
//   - "Outcome Intelligence"
//   - "Micro-Cap Signal"
//   - "Small-Cap Signal"
//   - "Large-Cap Signal"
//   - "phase12cfe-v1"

import { useEffect, useRef, useState } from "react";
import {
  getSignalLanesCandidates,
  getSignalLanesPerformance,
  getSignalLanesSummary,
  getSignalOutcomeLearningSummary,
  type LaneCandidates,
  type LaneCard,
  type LaneId,
  type LanePerformance,
  type LaneSummary,
  type OutcomeLearningSummary,
  type RoutedCandidate,
} from "../../lib/signalLanesClient";
import { SectionExplainer, TermHelp } from "../BeginnerHelp";

const POLL_MS = 60_000;
const TOP_ROWS = 5;

const PRIMARY_LANES: LaneId[] = [
  "micro_cap_signal",
  "small_cap_signal",
  "large_cap_signal",
];

function fmtPct(v: number | null | undefined): string {
  if (v === null || v === undefined) return "Pending";
  return `${(v * 100).toFixed(1)}%`;
}

function fmtInt(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return String(v);
}

function fmtPrice(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return `$${v.toFixed(2)}`;
}

function fmtScore(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return v.toFixed(3);
}

function fmtMarketCap(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  if (v >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(2)}B`;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(0)}M`;
  return `$${v.toFixed(0)}`;
}

const LANE_EMPTY_COPY: Record<LaneId, string> = {
  micro_cap_signal: "No Micro-Cap candidates currently on radar.",
  small_cap_signal: "No Small-Cap candidates currently on radar.",
  large_cap_signal: "No Large-Cap candidates currently on radar.",
  unknown_market_cap: "No candidates with unknown market cap.",
};

function CandidateTable({
  lane,
  rows,
  expanded,
  onToggle,
}: {
  lane: LaneId;
  rows: RoutedCandidate[];
  expanded: boolean;
  onToggle: () => void;
}) {
  if (rows.length === 0) {
    return (
      <p
        data-testid={`signal-lane-${lane}-empty-radar`}
        style={{ fontSize: 13, color: "#8a8f99", margin: "8px 0 0 0" }}
      >
        {LANE_EMPTY_COPY[lane]}
      </p>
    );
  }
  const visible = expanded ? rows : rows.slice(0, TOP_ROWS);
  return (
    <div data-testid={`signal-lane-${lane}-candidates`}>
      <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
      <table
        style={{ width: "100%", fontSize: 12.5, borderCollapse: "collapse", marginTop: 6 }}
        data-testid={`signal-lane-${lane}-candidate-table`}
      >
        <thead>
          <tr style={{ color: "#8a8f99", textAlign: "left", borderBottom: "1px solid #2a2f3a" }}>
            <th style={{ padding: "3px 4px", width: 28 }}>#</th>
            <th style={{ padding: "3px 4px" }}>Symbol</th>
            <th style={{ padding: "3px 4px", textAlign: "right" }}>Price</th>
            <th style={{ padding: "3px 4px", textAlign: "right" }}>Score</th>
            <th style={{ padding: "3px 4px", textAlign: "right" }}>Mkt cap</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((r) => (
            <tr
              key={r.symbol}
              data-testid={`signal-lane-${lane}-row-${r.symbol}`}
              style={{ borderBottom: "1px solid #1a1d24" }}
            >
              <td style={{ padding: "3px 4px", color: "#8a8f99" }}>
                {r.rank ?? "—"}
              </td>
              <td style={{ padding: "3px 4px", fontWeight: 600 }}>{r.symbol}</td>
              <td style={{ padding: "3px 4px", textAlign: "right" }}>
                {fmtPrice(r.price)}
              </td>
              <td style={{ padding: "3px 4px", textAlign: "right" }}>
                {fmtScore(r.score)}
              </td>
              <td
                style={{ padding: "3px 4px", textAlign: "right", color: "#8a8f99" }}
                title={r.lane_classification_reason}
              >
                {fmtMarketCap(r.market_cap_estimate_usd)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {rows.length > TOP_ROWS && (
        <button
          type="button"
          onClick={onToggle}
          data-testid={`signal-lane-${lane}-toggle`}
          style={{
            background: "transparent",
            color: "#6ec5ff",
            border: "none",
            padding: "6px 0",
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          {expanded
            ? `Show top ${TOP_ROWS}`
            : `Show all ${rows.length} (${rows.length - TOP_ROWS} more)`}
        </button>
      )}
    </div>
  );
}

function LaneCardView({
  card,
  candidates,
  expanded,
  onToggle,
}: {
  card: LaneCard;
  candidates: RoutedCandidate[];
  expanded: boolean;
  onToggle: () => void;
}) {
  const testIdBase = `signal-lane-${card.lane_id.replace("_signal", "")}`;
  return (
    <div
      data-testid={`${testIdBase}-card`}
      data-lane-id={card.lane_id}
      style={{
        border: "1px solid #2a2f3a",
        borderRadius: 8,
        padding: 14,
        background: "#13161e",
        flex: 1,
        minWidth: 280,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0, fontSize: 15 }} data-testid={`${testIdBase}-title`}>
          {card.lane_label}
        </h3>
        <span
          style={{ fontSize: 11, color: "#8a8f99" }}
          data-testid={`${testIdBase}-count`}
        >
          {card.today_candidate_count} on radar
        </span>
      </div>
      <p style={{ fontSize: 11.5, color: "#8a8f99", margin: "6px 0 8px 0" }}>
        {card.description}
      </p>

      <CandidateTable
        lane={card.lane_id}
        rows={candidates}
        expanded={expanded}
        onToggle={onToggle}
      />

      <details style={{ marginTop: 10 }}>
        <summary
          style={{ fontSize: 11, color: "#8a8f99", cursor: "pointer" }}
          data-testid={`${testIdBase}-metrics-toggle`}
        >
          Lane metrics
        </summary>
        <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse", marginTop: 6 }}>
          <tbody>
            <tr>
              <td style={{ color: "#8a8f99", padding: "2px 0" }}>Window N</td>
              <td style={{ textAlign: "right" }}>{fmtInt(card.window_candidate_count)}</td>
            </tr>
            <tr>
              <td style={{ color: "#8a8f99", padding: "2px 0" }}>Same-day hit rate</td>
              <td style={{ textAlign: "right" }}>{fmtPct(card.same_day_hit_rate)}</td>
            </tr>
            <tr>
              <td style={{ color: "#8a8f99", padding: "2px 0" }}>5-day hit rate</td>
              <td style={{ textAlign: "right" }}>{fmtPct(card.five_day_hit_rate)}</td>
            </tr>
            <tr>
              <td style={{ color: "#8a8f99", padding: "2px 0" }}>10-day hit rate</td>
              <td style={{ textAlign: "right" }}>{fmtPct(card.ten_day_hit_rate)}</td>
            </tr>
            <tr>
              <td style={{ color: "#8a8f99", padding: "2px 0" }}>False-negative</td>
              <td style={{ textAlign: "right" }}>{fmtPct(card.false_negative_rate)}</td>
            </tr>
            <tr>
              <td style={{ color: "#8a8f99", padding: "2px 0" }}>False-positive</td>
              <td style={{ textAlign: "right" }}>{fmtPct(card.false_positive_rate)}</td>
            </tr>
          </tbody>
        </table>
        </div>
        {card.sample_size_warning && (
          <p
            data-testid={`${testIdBase}-sample-warning`}
            style={{ fontSize: 11, color: "#e8b143", marginTop: 6 }}
          >
            Limited sample — treat metrics as preliminary.
          </p>
        )}
      </details>
    </div>
  );
}

function OutcomeIntelligenceView({
  data,
}: {
  data: OutcomeLearningSummary | null;
}) {
  if (!data) {
    return (
      <p
        style={{ fontSize: 13, color: "#8a8f99" }}
        data-testid="signal-outcome-intelligence-empty"
      >
        Outcome Intelligence is loading…
      </p>
    );
  }
  return (
    <div data-testid="signal-outcome-intelligence-panel">
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
        <Tile label="Dates tracked"   value={fmtInt(data.dates_tracked)} />
        <Tile label="Candidates"      value={fmtInt(data.candidates_tracked)} />
        <Tile label="Same-day hit"    value={fmtPct(data.same_day_hit_rate)} />
        <Tile label="5-day hit"       value={fmtPct(data.five_day_hit_rate)} />
        <Tile label="10-day hit"      value={fmtPct(data.ten_day_hit_rate)} />
        <Tile label="False negatives" value={fmtPct(data.false_negative_rate)} />
        <Tile label="False positives" value={fmtPct(data.false_positive_rate)} />
        <Tile label="Late winners"    value={fmtPct(data.late_winner_rate)} />
      </div>
      <p style={{ fontSize: 11.5, color: "#8a8f99" }}>
        Outcome Intelligence is advisory only. It does NOT modify the official
        Signal hit rate, official Signal labels, or Phase 13 readiness.
      </p>
    </div>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        border: "1px solid #2a2f3a",
        borderRadius: 6,
        padding: "6px 10px",
        background: "#0f1218",
        minWidth: 105,
      }}
    >
      <div style={{ fontSize: 11, color: "#8a8f99" }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 600 }}>{value}</div>
    </div>
  );
}

export default function SignalLanePanel() {
  const [summary, setSummary] = useState<LaneSummary | null>(null);
  const [perf, setPerf] = useState<LanePerformance | null>(null);
  const [cands, setCands] = useState<LaneCandidates | null>(null);
  const [outcome, setOutcome] = useState<OutcomeLearningSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<LaneId, boolean>>({
    micro_cap_signal: false,
    small_cap_signal: false,
    large_cap_signal: false,
    unknown_market_cap: false,
  });
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const tick = async () => {
      const [s, p, c, o] = await Promise.all([
        getSignalLanesSummary(),
        getSignalLanesPerformance(),
        getSignalLanesCandidates(),
        getSignalOutcomeLearningSummary(),
      ]);
      if (!mounted.current) return;
      if (s.ok === true) setSummary(s.data); else setErr(s.error.message);
      if (p.ok) setPerf(p.data);
      if (c.ok) setCands(c.data);
      if (o.ok) setOutcome(o.data);
    };
    tick();
    const id = window.setInterval(() => {
      if (!document.hidden) tick();
    }, POLL_MS);
    return () => {
      mounted.current = false;
      window.clearInterval(id);
    };
  }, []);

  // CRITICAL: group BEFORE display slicing. The backend already returns
  // rows_by_lane grouped; we just look up the lane bucket here and never
  // collapse into a single sorted list.
  const rowsFor = (lane: LaneId): RoutedCandidate[] =>
    (cands?.rows_by_lane?.[lane] as RoutedCandidate[] | undefined) ?? [];

  return (
    <section
      data-testid="signal-lane-panel"
      data-version="phase12cfe-v1"
      style={{ marginBottom: 24, color: "#dde2ea" }}
    >
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ margin: "0 0 4px 0", fontSize: 18 }}>
          Signal Lane Performance
        </h2>
        <p style={{ fontSize: 12, color: "#8a8f99", margin: 0 }}>
          Micro-Cap, Small-Cap, and Large-Cap Signal lanes are presented and
          scored independently. Each lane shows the actual candidates on
          radar. Advisory only — official Signal labels, scores, and hit rate
          are unchanged.
        </p>
      </div>

      {err && (
        <p style={{ color: "#e87a7a", fontSize: 13 }} data-testid="signal-lane-error">
          Could not load lane data: {err}
        </p>
      )}

      <div
        data-testid="signal-lane-cards"
        style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}
      >
        {summary?.lanes
          ?.filter((c) => PRIMARY_LANES.includes(c.lane_id))
          ?.map((card) => (
            <LaneCardView
              key={card.lane_id}
              card={card}
              candidates={rowsFor(card.lane_id)}
              expanded={expanded[card.lane_id] ?? false}
              onToggle={() =>
                setExpanded((p) => ({ ...p, [card.lane_id]: !p[card.lane_id] }))
              }
            />
          )) ?? (
          <p style={{ color: "#8a8f99", fontSize: 13 }}>Loading lanes…</p>
        )}
      </div>

      <div style={{ marginBottom: 8 }}>
        <h2 style={{ margin: "0 0 4px 0", fontSize: 18 }}>
          Outcome Intelligence
        </h2>
        <p style={{ fontSize: 12, color: "#8a8f99", margin: 0 }}>
          Learning summary over recent Signal candidates: hit rate, late
          winners, false negatives, false positives. Advisory only.
        </p>
      </div>
      <SectionExplainer
        testId="beginner-help-outcome-intelligence"
        title="Outcome Intelligence beginner guide"
        what={<>Outcome Intelligence reviews what happened after earlier Signal candidates.</>}
        why={<>It helps operators learn from follow-through, late winners, and missed outcomes without rewriting today&apos;s result tags.</>}
        lookAt={<>Look at the tracked dates, candidates, same-day hit, longer-window hit rates, false negatives, false positives, and late winners.</>}
        notDo={<>It does not modify official Signal labels, the official Signal hit rate, Phase 13 readiness, or Candidate Promotion.</>}
      >
        <TermHelp term="Outcome Intelligence" />
        <TermHelp term="Follow-Through" />
        <TermHelp term="Candidate Promotion" />
      </SectionExplainer>
      <OutcomeIntelligenceView data={outcome} />

      {perf?.limitations?.length ? (
        <details style={{ marginTop: 12 }}>
          <summary
            style={{ fontSize: 11, color: "#8a8f99", cursor: "pointer" }}
            data-testid="signal-lane-diagnostics-toggle"
          >
            Diagnostics
          </summary>
          <ul
            data-testid="signal-lane-limitations"
            style={{ fontSize: 11, color: "#8a8f99", marginTop: 6, paddingLeft: 18 }}
          >
            {perf.limitations.map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  );
}
